<script setup lang="ts">
// fallow-ignore-next-line unused-type -- used locally via defineProps<Props> (Fallow Vue false positive)
export interface Props {
	notifications?: [string, DisplayNotification[]][]
	notificationCount: number
}
const { data: deviceHardwareModels } = fetchHardwareModels()
const { notifications = [], notificationCount } = defineProps<Props>()
const { t } = useI18n()
const { setSuccessMsg } = useSnackbarStore()
const copyTraceId = (traceId: string) => {
	copy(traceId)
	setSuccessMsg(t("common.traceIdCopied", { traceId }))
}

const emit = defineEmits<{
	(e: "markAllAsRead" | "clearRead"): void
	(e: "markAsRead", id: string): void
}>()

// Clicking a notification row marks it as read (no navigation — the device/fleet links
// below still navigate on their own). No-op for already-read rows or rows without a stable
// id (legacy notifications created before ids were stamped).
const onRowClick = (item: DisplayNotification) => {
	if (!item.read && item.id) emit("markAsRead", item.id)
}

const notificationAction = async (fleetId: string, eventId: string) => {
	await navigateTo(
		{
			path: `/fleets/${fleetId}`,
			query: { eventId }
		},
		{
			open: {
				target: "_self"
			}
		}
	)
}
</script>

<template>
	<UPopover :open-delay="60" :close-delay="100" mode="hover">
		<!-- trigger -->
		<UChip inset color="error" :show="notificationCount > 0" :ui="{
			base: 'absolute -top-2 -right-1 min-w-[20px] h-[22px] px-1 rounded-full bg-error text-white text-[10px] leading-[18px] text-center font-bold',
		}">
			<UButton icon="i-mdi-bell-outline" variant="ghost" color="neutral"
				:aria-label="$t('notifications.title') ?? 'Notifications'" />

			<template #content>
				<div>{{ notificationCount }}</div>
			</template>
		</UChip>

		<!-- content -->
		<template #content>
			<div class="min-w-80 rounded-xl overflow-hidden">
				<!-- toolbar -->
				<div
					class="flex items-center justify-between gap-3 px-4 py-3 border-b border-default bg-gradient-to-r from-primary/5 to-secondary/5">
					<div class="flex items-center gap-2 min-w-0">
						<UIcon name="i-mdi-bell-outline" class="text-primary shrink-0" />
						<span class="font-semibold text-sm truncate">{{ $t("notifications.title") ?? "Notifications" }}</span>
					</div>
					<div class="flex items-center gap-1 shrink-0">
						<UButton icon="i-mdi-check-all" color="secondary" variant="soft" size="xs"
							:aria-label="$t('notifications.markAllRead')" :title="$t('notifications.markAllRead')"
							data-cy="clear-notifications" @click="emit('markAllAsRead')" />
						<UButton icon="i-mdi-broom" color="secondary" variant="soft" size="xs"
							:aria-label="$t('notifications.clearRead')" :title="$t('notifications.clearRead')"
							data-cy="clear-read-notifications" @click="emit('clearRead')" />
					</div>
				</div>

				<!-- empty state — show only when there are no notifications at all.
				     (notificationCount is the UNREAD count; read items must still render.) -->
				<div v-if="notifications.length === 0" class="py-10 text-center">
					<UIcon name="i-mdi-bell-check-outline" class="text-3xl text-muted mb-2" />
					<p class="text-sm text-muted">{{ $t("notifications.noNotifications") }}</p>
				</div>

				<!-- notification list -->
				<div v-else class="max-h-[65dvh] overflow-y-auto">
					<div v-for="entry in notifications" :key="entry[0]">
						<div
							class="text-[11px] font-semibold text-muted uppercase tracking-[0.12em] px-4 pt-3 pb-1.5 bg-elevated/30">
							{{ entry[0] }}
						</div>
						<div v-for="item in entry[1]" :key="item.traceId ?? item.title"
							class="px-4 py-3 hover:bg-elevated/40 transition-colors border-t border-default/50"
							:class="{ 'opacity-60': item.read, 'cursor-pointer': !item.read && item.id }"
							:data-cy="item.read ? 'notification-read' : 'notification-unread'"
							:title="!item.read && item.id ? $t('notifications.markRead') : undefined"
							@click="onRowClick(item)">
							<!-- title -->
							<div v-if="item.eventType?.endsWith('ImageRelease')"
								class="text-sm" :class="item.read ? 'font-normal text-muted' : 'font-semibold text-default'">
								{{deviceHardwareModels?.find((x: DeviceHardwareModel) => x.model ===
									item.releaseHardwareModel)?.displayName ?? item.releaseHardwareModel}} — {{
									item.title }}
							</div>
							<div v-else class="text-sm" :class="item.read ? 'font-normal text-muted' : 'font-semibold text-default'">
								{{ item.title }}
							</div>

							<!-- subtitle -->
							<div class="text-xs text-muted mt-1 flex items-center flex-wrap gap-x-1.5 gap-y-1">
								<span>{{ item.time }}</span>
								<span class="opacity-50">•</span>
								<span v-if="item.eventType?.endsWith('.ImageRelease')">
									{{ item.releaseVersion }}
								</span>
								<OmnectLink v-else-if="item.downloadLink"
									:to="`/fleets/${item.links.fleetId}?eventId=${item.links.eventId!}`"
									@click="notificationAction(item.links.fleetId, item.links.eventId!)">
									{{ item.links.deviceId }}
								</OmnectLink>
								<OmnectLink v-else :to="`/fleets/${item.links.fleetId}/devices/${item.links.deviceId}`">
									{{ item.links.deviceId }}
								</OmnectLink>
								<span class="opacity-50">•</span>
								<OmnectLink :to="`/fleets/${item.links.fleetId}`">
									{{ item.links.fleet }}
								</OmnectLink>
							</div>

							<!-- trace id row -->
							<div v-if="item.traceId && item.eventType?.toLowerCase().includes('error')"
								class="flex items-center gap-2 mt-2 rounded-md bg-elevated/50 px-2 py-1">
								<span class="text-[11px] font-mono text-muted truncate">{{ item.traceId }}</span>
								<UButton variant="ghost" size="xs" icon="i-mdi-content-copy" color="neutral"
									@click="copyTraceId(item.traceId)" />
							</div>

							<!-- download action -->
							<div v-if="item.downloadLink" class="mt-2">
								<UButton variant="soft" color="secondary" size="xs" icon="i-mdi-download"
									@click="notificationAction(item.links.fleetId, item.links.eventId!)" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
	</UPopover>
</template>

<style>
@keyframes swing {

	0%,
	100% {
		transform: rotate(10deg);
	}

	50% {
		transform: rotate(-10deg);
	}
}

.swing {
	animation: swing 200ms 5;
}
</style>
